import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import {
  ALL_STATUSES,
  STATUS_STYLES,
  SHORT_STATUS,
  ageFlag,
  isClosed,
  fmtDate,
} from "@/lib/status";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import StatusBadge, { PriorityBadge, TypeBadge, AgeBadge } from "@/components/StatusBadge";
import HoldReasonDialog from "@/components/HoldReasonDialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowUpDown, ArrowUp, ArrowDown, Search, PlusCircle, Download, Layers } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const RANGES = [
  { key: "all", label: "All time", days: null },
  { key: "week", label: "Week", days: 7 },
  { key: "fortnight", label: "Fortnight", days: 14 },
  { key: "month", label: "Month", days: 30 },
];

const COLUMNS = [
  { key: "job_number", label: "Job" },
  { key: "part_number", label: "Part" },
  { key: "part_name", label: "Part name" },
  { key: "request_type", label: "Type" },
  { key: "priority", label: "Priority" },
  { key: "rework_reason", label: "Reason" },
  { key: "status", label: "Status" },
  { key: "assigned_to", label: "Assigned" },
  { key: "required_by", label: "Required" },
  { key: "date_submitted", label: "Submitted" },
];

const MANUAL_STATUS_OPTIONS = ALL_STATUSES.filter((status) => !isClosed(status));

export default function Register() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [range, setRange] = useState("all");
  const [search, setSearch] = useState("");
  const [groupByJob, setGroupByJob] = useState(false);
  const [sort, setSort] = useState({ key: "date_submitted", dir: "desc" });
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [holdTarget, setHoldTarget] = useState(null);

  const canEdit = user?.role === "admin" || user?.role === "lead";
  const canSubmit = !["management", "kiosk"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter === "active") params.active_only = true;
      else if (statusFilter !== "all") params.status_filter = statusFilter;
      if (typeFilter !== "all") params.request_type = typeFilter;
      if (search) params.search = search;
      const { data } = await api.get("/reworks", { params });
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter, typeFilter]);

  const filtered = useMemo(() => {
    const cutoff = RANGES.find((r) => r.key === range)?.days;
    let list = rows;
    if (cutoff) {
      const since = Date.now() - cutoff * 86400000;
      list = list.filter((r) => new Date(r.date_submitted).getTime() >= since);
    }
    return [...list].sort((a, b) => {
      const av = a[sort.key] ?? ""; const bv = b[sort.key] ?? "";
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
  }, [rows, sort, range]);

  const groups = useMemo(() => {
    if (!groupByJob) return null;
    const map = {};
    filtered.forEach((r) => { (map[r.job_number] ||= []).push(r); });
    return Object.entries(map);
  }, [filtered, groupByJob]);

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const applyStatus = async (row, newStatus, hold_reason) => {
    setUpdatingId(row.id);
    try {
      const { data } = await api.patch(`/reworks/${row.id}`, { status: newStatus, hold_reason });
      setRows((rs) => rs.map((r) => (r.id === row.id ? data : r)));
      toast.success(`Status → ${SHORT_STATUS[newStatus] || newStatus}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally {
      setUpdatingId(null);
      setHoldTarget(null);
    }
  };

  const changeStatus = (row, newStatus) => {
    if (row.status === newStatus) return;
    if (newStatus === "On Hold") { setHoldTarget(row); return; }
    applyStatus(row, newStatus);
  };

  const exportCsv = async () => {
    const res = await api.get("/reworks/export.csv", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `misco-reworks-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const Row = ({ r }) => {
    const flag = ageFlag(r);
    return (
      <tr className="border-b border-[#e6e5df] hover:bg-[#efece5]/60 transition-colors" data-testid={`row-${r.id}`}>
        <td className="px-4 py-3 mono">
          <Link to={`/rework/${r.id}`} className="text-[#232826] hover:text-[#647265] font-medium" data-testid={`row-link-${r.id}`}>{r.job_number}</Link>
        </td>
        <td className="px-4 py-3 mono text-[#424e43]">{r.part_number}</td>
        <td className="px-4 py-3 text-[#232826] font-medium" data-testid={`row-part-name-${r.id}`}>{r.part_name || <span className="text-[#a39f98]">—</span>}</td>
        <td className="px-4 py-3">{r.request_type === "Missing" ? <TypeBadge type="Missing" /> : <span className="text-xs text-[#6b6a68]">Rework</span>}</td>
        <td className="px-4 py-3">{r.priority === "Normal" ? <span className="text-xs text-[#a39f98]">Normal</span> : <PriorityBadge priority={r.priority} />}</td>
        <td className="px-4 py-3 text-[#6b6a68]">{r.rework_reason}</td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {canEdit && !isClosed(r.status) ? (
              <Select value={r.status} onValueChange={(v) => changeStatus(r, v)} disabled={updatingId === r.id}>
                <SelectTrigger className={`h-7 w-[190px] text-[11px] font-medium uppercase tracking-wider rounded-sm ${STATUS_STYLES[r.status] || STATUS_STYLES.Pending}`} data-testid={`row-status-${r.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#e6e5df]">
                  {MANUAL_STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : <StatusBadge status={r.status} testid={`row-status-badge-${r.id}`} />}
            <AgeBadge flag={flag} testid={`row-age-${r.id}`} />
          </div>
        </td>
        <td className="px-4 py-3 text-[#6b6a68]">{r.assigned_to || <span className="italic text-[#a39f98]">—</span>}</td>
        <td className="px-4 py-3 text-[#6b6a68] font-mono text-xs">{fmtDate(r.required_by)}</td>
        <td className="px-4 py-3 text-[#6b6a68] font-mono text-xs">{fmtDate(r.date_submitted)}</td>
      </tr>
    );
  };

  return (
    <div>
      <PageHeader title="Rework Register" subtitle="Every rework and missing-part request in one place." testid="register-header"
        actions={
          <>
            <Button variant="ghost" onClick={exportCsv} data-testid="export-csv" className="text-[#6b6a68] hover:text-[#424e43] hover:bg-[#efece5] rounded-sm text-xs uppercase tracking-widest">
              <Download size={14} className="mr-2" /> CSV
            </Button>
            {canSubmit && (
              <Link to="/new">
                <Button className="bg-[#424e43] hover:bg-[#232826] text-[#f7f5f1] rounded-sm uppercase text-xs tracking-widest" data-testid="new-request-cta">
                  <PlusCircle size={14} className="mr-2" /> New Request
                </Button>
              </Link>
            )}
          </>
        } />

      <div className="p-6 md:p-10 space-y-5">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative flex-1 max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a39f98]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              placeholder="Search job, part, description…" data-testid="register-search" className="pl-9 bg-white border-[#cfcfc9] rounded-sm focus-visible:ring-[#647265]" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[210px] bg-white border-[#cfcfc9] rounded-sm text-[#232826]" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-[#e6e5df]">
              <SelectItem value="all" data-testid="filter-status-all">All statuses</SelectItem>
              <SelectItem value="active" data-testid="filter-status-active">Active only</SelectItem>
              {ALL_STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`filter-status-${s}`}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[150px] bg-white border-[#cfcfc9] rounded-sm text-[#232826]" data-testid="filter-type"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-[#e6e5df]">
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="Rework">Rework</SelectItem>
              <SelectItem value="Missing">Missing</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex bg-white border border-[#cfcfc9] rounded-sm overflow-hidden">
            {RANGES.map((r) => (
              <button key={r.key} onClick={() => setRange(r.key)} data-testid={`range-${r.key}`}
                className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors ${range === r.key ? "bg-[#424e43] text-[#f7f5f1]" : "text-[#6b6a68] hover:text-[#232826] hover:bg-[#efece5]"}`}>{r.label}</button>
            ))}
          </div>
          <button onClick={() => setGroupByJob((g) => !g)} data-testid="group-by-job"
            className={`px-4 py-2 text-xs uppercase tracking-widest border rounded-sm flex items-center gap-2 transition-colors ${groupByJob ? "bg-[#424e43] text-[#f7f5f1] border-[#424e43]" : "bg-white text-[#6b6a68] border-[#cfcfc9] hover:bg-[#efece5]"}`}>
            <Layers size={13} /> Group by job
          </button>
        </div>

        <div className="text-xs font-mono text-[#6b6a68]" data-testid="register-count">{filtered.length} record{filtered.length !== 1 ? "s" : ""}{groups ? ` · ${groups.length} jobs` : ""}</div>

        <div className="border border-[#e6e5df] rounded-sm overflow-hidden bg-white" data-testid="register-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#efece5]">
                <tr className="border-b border-[#e6e5df]">
                  {COLUMNS.map((c) => (
                    <th key={c.key} className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-[#6b6a68] font-medium select-none">
                      <button onClick={() => toggleSort(c.key)} data-testid={`sort-${c.key}`} className="inline-flex items-center gap-1.5 hover:text-[#424e43]">
                        {c.label}
                        {sort.key === c.key ? (sort.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />) : <ArrowUpDown size={11} className="opacity-50" />}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={COLUMNS.length} className="text-center py-16 text-[#6b6a68] font-mono text-xs">Loading records…</td></tr>}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={COLUMNS.length} className="p-4">
                    {rows.length === 0 && !search && statusFilter === "all" && typeFilter === "all" && range === "all"
                      ? <EmptyState testid="register-empty" />
                      : <div className="text-center py-12 text-[#a39f98] text-sm" data-testid="register-no-match">No requests match your filters.</div>}
                  </td></tr>
                )}
                {!loading && !groups && filtered.map((r) => <Row key={r.id} r={r} />)}
                {!loading && groups && groups.map(([job, list]) => (
                  <>
                    <tr key={`g-${job}`} className="bg-[#efece5]/70 border-b border-[#e6e5df]" data-testid={`job-group-${job}`}>
                      <td colSpan={COLUMNS.length} className="px-4 py-2 mono text-xs font-semibold text-[#424e43]">{job} <span className="text-[#a39f98] font-normal ml-2">{list.length} part{list.length !== 1 ? "s" : ""}</span></td>
                    </tr>
                    {list.map((r) => <Row key={r.id} r={r} />)}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <HoldReasonDialog open={!!holdTarget} onOpenChange={(o) => !o && setHoldTarget(null)} onConfirm={(reason) => applyStatus(holdTarget, "On Hold", reason)} busy={!!updatingId} />
    </div>
  );
}
